//@ts-nocheck
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { Label, Room, Wall } from "../components/Floorplan";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  addLabels,
  addRooms,
  addWalls,
  resetStates,
  setCleanUpDone,
  setWalls,
  setLabels,
  setRooms,
  setHeight,
  setWidth,
  setWindowAssets,
  setDoorAssets,
  setPlot,
} from "../../../redux/features/appState";
import { getRandomColor } from "../utils/helpers";
import { useRef } from "react"; // Import useRef
import { setFromNewInputEditor } from "../../../redux/features/graph";

export const scaleCoordinate = (
  coord: number,
  isX: boolean,
  width: number,
  height: number,
) => {
  const offset = isX
    ? (window.visualViewport?.width || 0) * 0.3
    : (window.visualViewport?.height || 0) * 0.3;
  return coord * ((window.visualViewport?.height * 0.5) / height) + offset;
};

export const createWallKey = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
) => {
  return x1 < x2 || (x1 === x2 && y1 < y2)
    ? `${x1},${y1}-${x2},${y2}`
    : `${x2},${y2}-${x1},${y1}`;
};

export const generateDoors = (walls: Wall[], fpHeight: number): any[] => {
  const viewportHeight = typeof window !== "undefined" && window.visualViewport
    ? window.visualViewport.height
    : typeof window !== "undefined" ? window.innerHeight : 1080;
  const safeHeight = fpHeight && fpHeight > 0 ? fpHeight : 1080;
  const scaleFactor = (viewportHeight * 0.5) / safeHeight;

  const sharedWallsMap = new Map<string, Wall[]>();
  walls?.forEach((wall) => {
    if (wall.roomIds && wall.roomIds.length >= 2) {
      const r1 = wall.roomIds[0];
      const r2 = wall.roomIds[1];
      const key = [r1, r2].sort().join("-");
      if (!sharedWallsMap.has(key)) {
        sharedWallsMap.set(key, []);
      }
      sharedWallsMap.get(key)!.push(wall);
    }
  });

  const autoGeneratedDoors: any[] = [];
  sharedWallsMap.forEach((wallsList) => {
    let longestWall = wallsList[0];
    let maxLen = 0;
    wallsList.forEach((w) => {
      const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
      if (len > maxLen) {
        maxLen = len;
        longestWall = w;
      }
    });

    const unscaledLen = maxLen / scaleFactor;
    if (unscaledLen >= 2.05) {
      const x = (longestWall.x1 + longestWall.x2) / 2;
      const y = (longestWall.y1 + longestWall.y2) / 2;
      const isVertical = Math.abs(longestWall.x1 - longestWall.x2) < Math.abs(longestWall.y1 - longestWall.y2);

      autoGeneratedDoors.push({
        id: uuidv4(),
        x,
        y,
        isVertical,
        swingSign: 1,
      });
    }
  });
  return autoGeneratedDoors;
};


export const useRoomCleanUp = () => {
  const [processedRooms, setProcessedRooms] = useState<Room[]>([]);
  const [processedWalls, setProcessedWalls] = useState<Wall[]>([]);
  const [processedLabels, setProcessedLabels] = useState<Label[]>([]);
  const [processedDoorAssets, setProcessedDoorAssets] = useState<any[]>([]); // Add state for processed door assets
  const [processedWindowAssets, setProcessedWindowAssets] = useState<any[]>([]); // Add state for processed window assets
  const index = useSelector((state: RootState) => state.graph.index);
  const floorPlans = useSelector((state: RootState) => state.graph.floorPlans);
  const documentId = useSelector((state: RootState) => state.graph.documentId);
  const selectedFloorPlan = useSelector(
    (state: RootState) => state?.graph?.selectedFloorPlan,
  );
  const doRoomCleanUp = useSelector(
    (state: RootState) => state.outputEditorButtonState.doRoomCleanUp,
  );
  const cleanUpDone = useSelector(
    (state: RootState) => state.outputEditorButtonState.cleanUpDone,
  );
  const fromDocuments = useSelector(
    (state: RootState) => state.graph.fromDocuments,
  );
  const fromNewInputEditor = useSelector(
    (state: RootState) => state.graph.fromNewInputEditor,
  );
  const appStateRooms = useSelector(
    (state: RootState) => state.outputEditorButtonState.rooms,
  );
  const appStateWalls = useSelector(
    (state: RootState) => state.outputEditorButtonState.walls,
  );
  const appStateLabels = useSelector(
    (state: RootState) => state.outputEditorButtonState.labels,
  );
  const roomNameMapping = useSelector(
  (state: RootState) => state.graph.roomNameMapping
);
  const floorplan_height = useSelector(
    (state: RootState) => state.outputEditorButtonState.height,
  );
  const floorplan_width = useSelector(
    (state: RootState) => state.outputEditorButtonState.width,
  );
  const plotWidthState = useSelector(
    (state: any) => state.nodesState.plotWidth,
  );
  const plotHeightState = useSelector(
    (state: any) => state.nodesState.plotHeight,
  );
  const nodes = useSelector(
    (state: RootState) => state.nodesState.nodes,
  );
  const appStateDoorAssets = useSelector(
    // Add selector for door assets
    (state: RootState) => state.outputEditorButtonState.doorAssets,
  );
  const appStateWindowAssets = useSelector(
    // Add selector for window assets
    (state: RootState) => state.outputEditorButtonState.windowAssets,
  );

  const dispatch = useDispatch<AppDispatch>();
  const cleanupPerformed = useRef(false); // Ref to track if cleanup has been performed

  // Reset cleanupPerformed when doRoomCleanUp is activated
  useEffect(() => {
    if (doRoomCleanUp) {
      cleanupPerformed.current = false;
    }
  }, [doRoomCleanUp]);

  useEffect(() => {
    if (fromNewInputEditor) {
      dispatch(setFromNewInputEditor(false));
      const autoGeneratedDoors = generateDoors(appStateWalls || [], floorplan_height);
      setProcessedDoorAssets(autoGeneratedDoors);
      dispatch(setDoorAssets(autoGeneratedDoors));
      return;
    }

    if (!doRoomCleanUp || cleanupPerformed.current) {
      return;
    }; // Only run if cleanup is needed and not yet performed

    cleanupPerformed.current = true; // Mark cleanup as performed for this cycle
    if (
      fromDocuments &&
      localStorage.getItem("lastProcessed") === String(documentId)
    ) {
      return;
    }
    if (fromDocuments) {
      const width = floorplan_width || 1; // Fallback to 1 to prevent division by zero
      const height = floorplan_height || 1;

      let scaledWalls: Wall[] = [];

      appStateWalls?.forEach((wall) => {
        scaledWalls.push({
          id: wall.id,
          roomIds: wall.roomIds,
          x1: scaleCoordinate(wall.x1, true, width, height),
          x2: scaleCoordinate(wall.x2, true, width, height),
          y1: scaleCoordinate(wall.y1, false, width, height),
          y2: scaleCoordinate(wall.y2, false, width, height),
        });
      });

      let scaledRooms: Room[] = [];
      appStateRooms?.forEach((room) => {
        let scaledWallsOfRooms: Wall[] = [];
        room.walls?.forEach((wall) => {
          scaledWallsOfRooms.push({
            id: wall.id,
            roomIds: wall.roomIds,
            x1: scaleCoordinate(wall.x1, true, width, height),
            x2: scaleCoordinate(wall.x2, true, width, height),
            y1: scaleCoordinate(wall.y1, false, width, height),
            y2: scaleCoordinate(wall.y2, false, width, height),
          });
        });
        scaledRooms.push({ ...room, walls: scaledWallsOfRooms });
      });
      let scaledLabels: Label[] = [];
      appStateLabels?.forEach((label) => {
        scaledLabels.push({
          ...label,
          x: scaleCoordinate(label.x, true, width, height),
          y: scaleCoordinate(label.y, false, width, height),
        });
      });
      let scaledDoors:
        | Array<{ id: string; x: number; y: number; isVertical: boolean }>
        | null
        | undefined = [];
      appStateDoorAssets?.forEach((door) => {
        scaledDoors?.push({
          ...door,
          x: scaleCoordinate(door.x, true, width, height),
          y: scaleCoordinate(door.y, false, width, height),
        });
      });
      let scaledWindows:
        | Array<{ id: string; x: number; y: number; isVertical: boolean }>
        | null
        | undefined = [];
      appStateWindowAssets?.forEach((window) => {
        scaledWindows?.push({
          ...window,
          x: scaleCoordinate(window.x, true, width, height),
          y: scaleCoordinate(window.y, false, width, height),
        });
      });

      setProcessedRooms(scaledRooms || []);
      setProcessedWalls(scaledWalls || []);
      setProcessedLabels(scaledLabels || []);
      setProcessedDoorAssets(scaledDoors || []); // Set processed door assets
      setProcessedWindowAssets(scaledWindows || []); // Set processed window assets
      dispatch(setWindowAssets(scaledWindows));
      dispatch(setDoorAssets(scaledDoors));
      dispatch(setRooms(scaledRooms));
      dispatch(setWalls(scaledWalls));
      dispatch(setLabels(scaledLabels));
      // Width and Height are already correctly set in Redux from backendPlotWidth/Height
      dispatch(setCleanUpDone(true)); // Mark cleanup as done immediately for saved documents
      localStorage.setItem("lastProcessed", String(documentId));
    } else {
      // --- START: User's original processFloorPlan logic for Input Editor path ---
      localStorage.removeItem("lastProcessed");
      console.log("useRoomCleanUp: Processing from Input Editor.", {
        selectedFloorPlan,
        floorPlans,
        index,
      });
      const processFloorPlan = () => {
        // Debug: Log data shape
        if (selectedFloorPlan && selectedFloorPlan.length > 0) {
          const sampleRoom = selectedFloorPlan[0];
          console.log("useRoomCleanUp: Sample room data:", {
            _id: sampleRoom._id,
            name: sampleRoom.name,
            wallCount: sampleRoom.walls?.length,
            sampleWall: sampleRoom.walls?.[0],
          });
        } else {
          console.log("useRoomCleanUp: selectedFloorPlan is empty or null:", selectedFloorPlan);
        }
        // Only reset states when processing from Input Editor to ensure a clean slate
        dispatch(resetStates()); // Reintroduce resetStates for Input Editor path
        const rooms: Room[] = [];
        const wallMap = new Map<string, Wall & { roomIds: string[] }>();
        const labels: Label[] = [];
        let minX = 1e9;
        let maxX = -1e9;
        let minY = 1e9;
        let maxY = -1e9;

        // Calculate min/max coordinates using selectedFloorPlan (Input Editor data)
        (selectedFloorPlan || []).forEach((apiRoom) => {
          (apiRoom.walls || []).forEach((apiWall: any) => {
            minX = Math.min(apiWall.x1, apiWall.x2, minX);
            minY = Math.min(apiWall.y1, apiWall.y2, minY);
            maxX = Math.max(apiWall.x1, apiWall.x2, maxX);
            maxY = Math.max(apiWall.y1, apiWall.y2, maxY);
          });
        });
        const gridWidth = Math.abs(maxX - minX);
        const gridHeight = Math.abs(maxY - minY);

        let scaleX = 10;
        let scaleY = 10;

        if (plotWidthState && plotHeightState && plotWidthState > 0 && plotHeightState > 0) {
          scaleX = plotWidthState / gridWidth;
          scaleY = plotHeightState / gridHeight;
        } else {
          // Fallback: Scale grid based on user configured room min dimensions
          let maxMinWidth = 10;
          let maxMinHeight = 10;
          (nodes || []).forEach((node: any) => {
            if (node.width?.min > 0) {
              maxMinWidth = Math.max(maxMinWidth, node.width.min);
            }
            if (node.height?.min > 0) {
              maxMinHeight = Math.max(maxMinHeight, node.height.min);
            }
          });

          let maxRoomGridWidth = 1;
          let maxRoomGridHeight = 1;
          (selectedFloorPlan || []).forEach((apiRoom) => {
            if (apiRoom.width > 0) {
              maxRoomGridWidth = Math.max(maxRoomGridWidth, apiRoom.width);
            }
            if (apiRoom.height > 0) {
              maxRoomGridHeight = Math.max(maxRoomGridHeight, apiRoom.height);
            }
          });

          scaleX = maxMinWidth / maxRoomGridWidth;
          scaleY = maxMinHeight / maxRoomGridHeight;
        }

        const width = gridWidth * scaleX;
        const height = gridHeight * scaleY;
        console.log("HEIGHT----------->>>>>>WIDTH", height, width);
        dispatch(setHeight(height));
        dispatch(setWidth(width));

        if (plotWidthState && plotHeightState && plotWidthState > 0 && plotHeightState > 0) {
          const plotWidthScaled = scaleCoordinate(plotWidthState, true, width, height) - scaleCoordinate(0, true, width, height);
          const plotHeightScaled = scaleCoordinate(plotHeightState, false, width, height) - scaleCoordinate(0, false, width, height);

          const minFPX = scaleCoordinate(0, true, width, height);
          const maxFPX = scaleCoordinate(width, true, width, height);
          const minFPY = scaleCoordinate(0, false, width, height);
          const maxFPY = scaleCoordinate(height, false, width, height);

          const wall1: Wall = {
            id: `plot-${uuidv4()}`,
            roomIds: [],
            x2: (maxFPX + minFPX + plotWidthScaled) / 2,
            x1: (maxFPX + minFPX - plotWidthScaled) / 2,
            y1: (maxFPY + minFPY - plotHeightScaled) / 2,
            y2: (maxFPY + minFPY - plotHeightScaled) / 2,
          };
          const wall2: Wall = {
            id: `plot-${uuidv4()}`,
            roomIds: [],
            x1: (maxFPX + minFPX + plotWidthScaled) / 2,
            x2: (maxFPX + minFPX - plotWidthScaled) / 2,
            y1: (maxFPY + minFPY + plotHeightScaled) / 2,
            y2: (maxFPY + minFPY + plotHeightScaled) / 2,
          };
          const wall3: Wall = {
            id: `plot-${uuidv4()}`,
            roomIds: [],
            x1: (maxFPX + minFPX - plotWidthScaled) / 2,
            x2: (maxFPX + minFPX - plotWidthScaled) / 2,
            y1: (maxFPY + minFPY - plotHeightScaled) / 2,
            y2: (maxFPY + minFPY + plotHeightScaled) / 2,
          };
          const wall4: Wall = {
            id: `plot-${uuidv4()}`,
            roomIds: [],
            x1: (maxFPX + minFPX + plotWidthScaled) / 2,
            x2: (maxFPX + minFPX + plotWidthScaled) / 2,
            y1: (maxFPY + minFPY - plotHeightScaled) / 2,
            y2: (maxFPY + minFPY + plotHeightScaled) / 2,
          };

          const newPlotBoundary = [wall1, wall2, wall3, wall4];
          dispatch(setPlot(newPlotBoundary));
        } else {
          dispatch(setPlot([]));
        }

        if ((selectedFloorPlan || []).length === 0) return;
         (selectedFloorPlan || []).forEach((apiRoom) => {
          console.log("API room data:", {
          name: apiRoom.name,
          roomtype: apiRoom.roomtype,
          type: apiRoom.type,
          category: apiRoom.category,
  });
          const room: Room = {
            id: apiRoom._id,
             name: apiRoom.original_name ?? 
            (roomNameMapping && Object.keys(roomNameMapping).length > 0
            ? roomNameMapping[parseInt(apiRoom.name)] ?? apiRoom.name
            : apiRoom.name),
            color: getRandomColor(),
            walls: [],
          };

          (apiRoom?.walls || []).forEach((apiWall: any) => {
            const x1 = scaleCoordinate((apiWall.x1 - minX) * scaleX, true, width, height);
            const y1 = scaleCoordinate((apiWall.y1 - minY) * scaleY, false, width, height);
            const x2 = scaleCoordinate((apiWall.x2 - minX) * scaleX, true, width, height);
            const y2 = scaleCoordinate((apiWall.y2 - minY) * scaleY, false, width, height);

            const wallKey = createWallKey(x1, y1, x2, y2);

            if (!wallMap.has(wallKey)) {
              const wall: Wall & { roomIds: string[] } = {
                id: apiWall._id,
                x1,
                y1,
                x2,
                y2,
                roomId: room.id,
                roomIds: [room.id],
              };
              wallMap.set(wallKey, wall);
            } else {
              const existingWall = wallMap.get(wallKey)!;
              if (!existingWall.roomIds.includes(room.id)) {
                existingWall.roomIds.push(room.id);
              }
            }
          });

          rooms.push(room);

          // Calculate center of a rectangle of the room for label placement
          const xPos = new Map<number, number[]>();
          (apiRoom?.walls || []).forEach((wall) => {
            if (xPos.has(wall.x1)) {
              let y = xPos.get(wall.x1);
              y?.push(wall.y1);
              xPos.set(wall.x1, y as number[]);
            } else {
              xPos.set(wall.x1, [wall.y1]);
            }
            if (xPos.has(wall.x2)) {
              let y = xPos.get(wall.x2);
              y?.push(wall.y2);
              xPos.set(wall.x2, y as number[]);
            } else {
              xPos.set(wall.x2, [wall.y2]);
            }
          });
          let x1Coord: number,
            x2Coord: number,
            y1Coord: number = Number.MAX_VALUE,
            y2Coord: number = Number.MIN_VALUE;
          x1Coord = xPos.keys().next().value;
          (xPos.get(x1Coord) || []).forEach((y) => {
            y1Coord = Math.min(y1Coord, y);
            y2Coord = Math.max(y2Coord, y);
          });
          xPos.delete(x1Coord);
          x2Coord = xPos.keys().next().value;
          (xPos.get(x2Coord) || []).forEach((y) => {
            y1Coord = Math.min(y1Coord, y);
            y2Coord = Math.max(y2Coord, y);
          });
          x1Coord = scaleCoordinate((x1Coord - minX) * scaleX, true, width, height);
          x2Coord = scaleCoordinate((x2Coord - minX) * scaleX, true, width, height);
          y1Coord = scaleCoordinate((y1Coord - minY) * scaleY, false, width, height);
          y2Coord = scaleCoordinate((y2Coord - minY) * scaleY, false, width, height);

          labels.push({
            x: (x1Coord + x2Coord) / 2,
            y: (y1Coord + y2Coord) / 2,
            text: room.name,
            roomId: room?.id,
          });
        });

        // Process walls to split larger walls and remove redundant pieces
        const finalWalls: Wall[] = [];
        const processedWallKeys = new Map<string, Wall>();

        wallMap.forEach((wall) => {
          if (wall.x1 === wall.x2 && wall.y1 === wall.y2) {
            // Skip points
            return;
          }

          const isHorizontal = wall.y1 === wall.y2;
          const wallStart = isHorizontal ? wall.x1 : wall.y1;
          const wallEnd = isHorizontal ? wall.x2 : wall.y2;

          // Find all intersection points
          const intersections = new Set<number>([wallStart, wallEnd]);
          wallMap.forEach((otherWall) => {
            if (
              isHorizontal &&
              otherWall.y1 === wall.y1 &&
              otherWall.y2 === wall.y2
            ) {
              if (
                Math.abs(otherWall.x1 - wall.x1) +
                Math.abs(otherWall.x1 - wall.x2) ===
                Math.abs(wall.x1 - wall.x2)
              ) {
                intersections.add(otherWall.x1);
              }
              if (
                Math.abs(otherWall.x2 - wall.x1) +
                Math.abs(otherWall.x2 - wall.x2) ===
                Math.abs(wall.x1 - wall.x2)
              ) {
                intersections.add(otherWall.x2);
              }
            } else if (
              !isHorizontal &&
              otherWall.x1 === wall.x1 &&
              otherWall.x2 === wall.x2
            ) {
              if (
                Math.abs(otherWall.y1 - wall.y1) +
                Math.abs(otherWall.y1 - wall.y2) ===
                Math.abs(wall.y1 - wall.y2)
              ) {
                intersections.add(otherWall.y1);
              }
              if (
                Math.abs(otherWall.y2 - wall.y1) +
                Math.abs(otherWall.y2 - wall.y2) ===
                Math.abs(wall.y1 - wall.y2)
              ) {
                intersections.add(otherWall.y2);
              }
            }
          });

          // Sort intersection points
          const sortedIntersections = Array.from(intersections).sort(
            (a, b) => a - b,
          );

          // Create wall segments
          for (let i = 0; i < sortedIntersections.length - 1; i++) {
            const start = sortedIntersections[i];
            const end = sortedIntersections[i + 1];
            if (start === end) continue; // Skip zero-length segments

            const x1: number = isHorizontal ? start : wall.x1;
            const y1: number = isHorizontal ? wall.y1 : start;
            const x2: number = isHorizontal ? end : wall.x2;
            const y2: number = isHorizontal ? wall.y2 : end;
            let roomIds = new Set<number>();

            // -----------------------------------------------------------------------------------------
            // --------------------------------THIS NEEDS OPTIMIZARION----------------------------------
            // -----------------------------------------------------------------------------------------
            wallMap.forEach((w) => {
              const isWallHorizontal: boolean = w.y1 === w.y2;
              if (isHorizontal === isWallHorizontal) {
                if (isWallHorizontal && y1 === w.y1) {
                  const x1Inside: boolean =
                    Math.abs(w.x1 - x1) + Math.abs(w.x2 - x1) ===
                    Math.abs(w.x1 - w.x2);
                  const x2Inside: boolean =
                    Math.abs(w.x1 - x2) + Math.abs(w.x2 - x2) ===
                    Math.abs(w.x1 - w.x2);
                  if (x1Inside && x2Inside) {
                    (w.roomIds || []).forEach((rid) => {
                      roomIds.add(rid);
                    });
                  }
                } else if (!isWallHorizontal && x1 === w.x1) {
                  const y1Inside: boolean =
                    Math.abs(w.y1 - y1) + Math.abs(w.y2 - y1) ===
                    Math.abs(w.y1 - w.y2);
                  const y2Inside: boolean =
                    Math.abs(w.y1 - y2) + Math.abs(w.y2 - y2) ===
                    Math.abs(w.y1 - w.y2);
                  if (y1Inside && y2Inside) {
                    (w.roomIds || []).forEach((rid) => {
                      roomIds.add(rid);
                    });
                  }
                }
              }
            });
            // -----------------------------------------------------------------------------------------
            // -----------------------------------------------------------------------------------------

            const newWall: Wall = {
              id: uuidv4(),
              x1: x1,
              y1: y1,
              x2: x2,
              y2: y2,
              roomIds: [...roomIds],
            };

            const wallKey = createWallKey(
              newWall.x1,
              newWall.y1,
              newWall.x2,
              newWall.y2,
            );
            if (!processedWallKeys.has(wallKey)) {
              processedWallKeys.set(wallKey, newWall);
            } else {
              const w = processedWallKeys.get(wallKey);
              (wall?.roomIds || []).forEach((rid) => {
                if (!w?.roomIds?.includes(rid)) {
                  w?.roomIds?.push(rid);
                }
              });
              processedWallKeys.set(wallKey, w as Wall);
            }
          }
        });

        processedWallKeys.forEach((val, key) => {
          finalWalls.push(val);
        });

        // Assign walls to rooms
        rooms.forEach((room) => {
          room.walls = finalWalls.filter((wall) =>
            (wall?.roomIds || []).includes(room.id),
          );
        });

        const autoGeneratedDoors = generateDoors(finalWalls, height);

        setProcessedRooms(rooms);
        setProcessedWalls(finalWalls);
        setProcessedLabels(labels);
        setProcessedDoorAssets(autoGeneratedDoors);
        dispatch(setRooms(rooms)); // Dispatch processed rooms to Redux
        dispatch(setWalls(finalWalls)); // Dispatch processed walls to Redux
        dispatch(setLabels(labels)); // Dispatch processed labels to Redux
        dispatch(setDoorAssets(autoGeneratedDoors)); // Dispatch auto-generated doors to Redux
        dispatch(setCleanUpDone(true)); // Mark cleanup as done for input editor path
      };
      processFloorPlan();
    }
  }, [
    doRoomCleanUp,
    fromDocuments,
    selectedFloorPlan,
    index,
    dispatch,
    fromNewInputEditor,
    appStateWalls,
    appStateDoorAssets,
    appStateLabels,
    appStateRooms,
    appStateWindowAssets,
    documentId,
    floorPlans,
    floorplan_height,
    floorplan_width,
    plotHeightState,
    plotWidthState,
    roomNameMapping,
    nodes,
  ]);

  // Removed this useEffect as it was causing data duplication for saved documents.
  // For saved documents, appState is already set by YourDocuments.tsx.
  // For input editor, processedRooms/Walls/Labels are dispatched directly.
  // useEffect(() => {
  //   if (processedRooms.length > 0) {
  //     dispatch(setRooms(processedRooms));
  //     dispatch(setWalls(processedWalls));
  //     dispatch(setLabels(processedLabels));
  //   }
  // }, [processedRooms, processedWalls, processedLabels, dispatch]);
};
